import React, { useEffect, useState } from "react"
import _ from 'lodash'
import Isvg from 'react-inlinesvg'

import { NavItemContainer_s } from "components/folder/explorer/folder-explorer-styles"
import { Home, CaretDownIcon, Folder as FolderIcon } from "images/icons/tools/"
import { CaretRightIcon } from "images/icons/general"

import folderService, { Folder } from "data/folder"
import { alphanum } from "components/table/lib/sort"
import notificationService from "data/notification"

type IFolderNav = {
  className?: string
  selectedFolder?: number | null
  openFolder?: number | null
  onSelectFolder: Function
  onFocusFolder?: Function
  onContext?: Function
  onDrop?: Function
  searchTerm?: string
  assetsOnly?: boolean
  minWidth?: number
  minHeight?: number
  maxHeight?: number
}

const FolderNav = (props: IFolderNav) => {
  const [folders, setFolders] = useState<object[]|null>(null)
  const [openFolders, setOpenFolders] = useState<object>({})
  const [focusFolder, setFocusFolder] = useState<number | null>(props.selectedFolder || 0)
  const [renameCode, setRenameCode] = useState<string | null>(null)

  useEffect(() => {
    const folderSubscription = folderService.stream().subscribe(data => {
      if (data.state === "ok"){
        let folders = [...data.value].sort((a, b) => alphanum(_.get(a, "code"), _.get(b, "code")))
        let renameFolder = _.find(folders, (f) => f.rename)
        if(props.assetsOnly) {
          const amId = _.get(_.find(folders, (f) => f.code === "*AssetManagement"), "folderid")
          if(amId) {
            const subtree = folderService.getFieldSubtree(amId, "folderid")
            folders = _.filter(folders, (f) => _.includes(subtree, f.folderid))
            let newOpen = _.cloneDeep(openFolders)
            newOpen[amId] = true
            setOpenFolders(newOpen)
          }
        } if(renameFolder) {
          setFocusFolder(renameFolder.folderid)
          setRenameCode(renameFolder.code)
        }
        setFolders(folders)
      }
    })

    folderService.fetchAllFolders()

    return function cleanup() {
      folderSubscription.unsubscribe()
    }
  }, [])

  useEffect(() => {
    const newOpen = _.cloneDeep(openFolders)
    let openPath = folderService.getIdPath(props.selectedFolder)
    openPath.pop()
    _.each(openPath, (p) => newOpen[p] = true)
    setOpenFolders(newOpen)
  }, [focusFolder])

  useEffect(() => {
    if(!renameCode) return
    setTimeout(() => {
      let input = document.querySelector("#rename-folder") as HTMLInputElement
      if(input) {
        input?.focus()
        input?.select()
      }
    }, 100)
    setRenameCode(null)
  }, [renameCode])

  useEffect(() => {
    if(props.onFocusFolder) setFocusFolder(_.get(props, "selectedFolder"))
  }, [props.selectedFolder])

  useEffect(() => {
    if(props.openFolder) {
      const newOpen = _.cloneDeep(openFolders)
      let openPath = folderService.getIdPath(props.selectedFolder)
      _.each(openPath, (p) => newOpen[p] = true)
      setOpenFolders(newOpen)
    }
  }, [props.openFolder])

  const setOpenStatus = (folderid) => {
    const newOpen = _.cloneDeep(openFolders)
    const toggleOpen = openFolders[folderid] ? true : false
    newOpen[folderid] = !toggleOpen
    setOpenFolders(newOpen)
  }

  const changeFocus = (folderid) => {
    setFocusFolder(folderid)
    if(props.onFocusFolder) props.onFocusFolder(folderid)
  }

  const handleRename = (folder, val) => {
    const existing = _.find(folderService.value(), {code: val, parentfolder: folder.parentfolder})
    if (existing && existing !== folder) {
      delete folder.rename
      props.onFocusFolder?.(folder.folderid)
      notificationService.notify({
        type: "warn",
        message: "Duplicate Folder Code - Unable to Rename Item",
        dismiss: 3000
      })
    } else {
      const newFolder = _.cloneDeep(folder)
      _.set(newFolder, "code", val)
      folderService.patchFolder(newFolder)
      props.onFocusFolder?.(folder.folderid)
    }
  }

  const navItems = (parentFolderId) => _.chain(folders)
    .filter((f) => _.get(f, "parentfolder") === parentFolderId)
    .map((f) => {
      let inSearch = false
      if(props.searchTerm) {
        const subtree = folderService.getFieldSubtree(f.folderid, "code")
        inSearch = _.some(subtree, (val) => _.includes(_.toLower(val), _.toLower(props.searchTerm)))
        if(!inSearch) return null
      }
      const hasChildren = _.some(folders, x => x.parentfolder == f.folderid)
      // const children = navItems(f.folderid)
      const idPath = folderService.getIdPath(focusFolder)
      idPath.pop() // the selected folder doesnt need to be open, and will always be last in the path
      const toggleOpen = inSearch || _.includes(idPath, f.folderid) || openFolders[f.folderid] ? true : false
      const displayChildren = toggleOpen ? navItems(f.folderid) : []
      const caretIcon = hasChildren && toggleOpen
        ? <Isvg src={CaretDownIcon} className="icon" />
        : hasChildren
        ? <Isvg src={CaretRightIcon} className="icon" />
        : null
      const folderIcon = <Isvg src={FolderIcon} className="icon" />
      const contentClass = focusFolder === f.folderid ? "li-content focused" : "li-content"
      const highlightCode = inSearch && _.includes(_.toLower(f.code), _.toLower(props.searchTerm)) ? "highlight" : ""
      return (
        <li key={`folder-${f.code}-${f.folderid}`} className="li-container">
          <span className={contentClass}
            onClick={() => changeFocus(f.folderid)}
            onDoubleClick={() => {
              if(f.rename) return
              props.onSelectFolder(f.folderid)
              setOpenStatus(f.folderid)
            }}
            onDragOver={(e) => e.preventDefault()}
            onDragEnter={(e) => {
              (e.target as HTMLSpanElement).classList.add('dragover');
            }}
            onDragLeave={(e) => {
              (e.target as HTMLSpanElement).classList.remove('dragover');
            }}
            onDrop={(e) => {
              (e.target as HTMLSpanElement).classList.remove('dragover');
              props.onDrop && props.onDrop(e, f.folderid, f.code)
            }}
            onContextMenu={(e) => {
              e.preventDefault()
              props.onContext ? props.onContext(e, f.folderid, f.code) : null
              return false
          }}>
            <span className="caret-icon" onClick={() => setOpenStatus(f.folderid)}>{ caretIcon }</span>
            <span className="folder-icon">{ folderIcon }</span>
            {f.rename ?
              <input
                id="rename-folder"
                type="text"
                defaultValue={f.code}
                onKeyDown={(e) => {
                  if(!['Enter', 'Tab'].includes(_.get(e, "key"))) return null
                  e.preventDefault()
                  e.stopPropagation()
                  _.invoke(e, 'target.blur')
                }}
                onBlur={(e) => handleRename(f, e.target.value)}/>
              :
              <span className="code"><span className={highlightCode}>{ f.code }</span></span>
            }
          </span>
          <ul className="children">
            { displayChildren }
          </ul>
        </li>
      )
    })
    .filter((f) => f !== null)
    .value()

  const containerProps = {
    className: props.className,
    midWidth: props.minWidth,
    minHeight: props.minHeight,
    maxHeight: props.maxHeight
  }

  return (
    <NavItemContainer_s {...containerProps}>
      <ul>
        <li onClick={() => changeFocus(null)} onContextMenu={(e) => {
          e.preventDefault()
          props.onContext ? props.onContext(e, null, "rDCF") : null
          return false
        }}>
          <span className="root">
            <Isvg key={Home} src={Home} className="home-icon" />
            <span className="root-name">rDCF</span>
          </span>
        </li>
        <ul>
          { navItems(null) }
        </ul>
      </ul>
    </NavItemContainer_s>
  )
}

export default FolderNav
